<template>
  <div class="assessment-layout">
    <!-- Add Sidebar component -->
    <Sidebar />
    
    <main class="assessment-content">
      <div class="header-row">
        <h1>Assessment Parts</h1>
        <button 
          @click="showCreateForm = true" 
          class="create-button"
        >
          Create New
        </button>
      </div>
      
      <div v-if="loading" class="text-h6">Loading assessment parts...</div>
      <div v-if="error" class="text-error">Error: {{ error }}</div>

      <div v-if="showCreateForm" class="card mb-4">
        <div class="card-body">
          <div class="mb-3">
            <input 
              v-model="newPart.title" 
              class="form-control mb-3"
              placeholder="Title"
            >
            <input 
              v-model="newPart.title_ar" 
              class="form-control mb-3"
              placeholder="Title Arabic"
            >
            <input 
              v-model="newPart.description" 
              class="form-control mb-3"
              placeholder="Description"
            >
            <input 
              v-model="newPart.description_ar" 
              class="form-control"
              placeholder="Description Arabic"
            >
          </div>
          <div class="d-flex gap-2">
            <button @click="createPart" class="btn btn-primary">
              <i class="bi bi-plus-circle me-2"></i>Create
            </button>
            <button @click="cancelCreate" class="btn btn-outline-secondary">
              Cancel
            </button>
          </div>
        </div>
      </div>

      <div v-else>
        <div v-for="part in parts" :key="part.id" class="option-item">
          <template v-if="editingId === part.id">
            <div class="edit-form">
              <div class="mb-3">
                <input 
                  v-model="editingValues.title" 
                  class="form-control mb-3"
                  placeholder="Title"
                >
                <input 
                  v-model="editingValues.title_ar" 
                  class="form-control mb-3"
                  placeholder="Title Arabic"
                >
                <input 
                  v-model="editingValues.description" 
                  class="form-control mb-3"
                  placeholder="Description"
                >
                <input 
                  v-model="editingValues.description_ar" 
                  class="form-control"
                  placeholder="Description Arabic"
                >
              </div>
              <div class="d-flex gap-2">
                <button @click="savePart(part.id)" class="btn btn-success">
                  <i class="bi bi-check-circle me-2"></i>Save
                </button>
                <button @click="cancelEdit" class="btn btn-secondary">
                  <i class="bi bi-x-circle me-2"></i>Cancel
                </button>
              </div>
            </div>
          </template>
          <template v-else>
            <h3 class="question">
              Title: {{ part.title }} 
            </h3>
            <h3 class="question">
              Title Arabic: {{ part.title_ar }} 
            </h3>
            <h3 class="question">
              Description: {{ part.description }} 
            </h3>
            <h3 class="question">
              Description Arabic: {{ part.description_ar }} 
            </h3>
            <div class="action-buttons">
              <button @click="startEditing(part)" class="btn btn-sm btn-outline-primary">
                <i class="bi bi-pencil-square me-2"></i>Edit
              </button>
              <button @click="deletePart(part.id)" class="btn btn-sm btn-outline-danger">
                <i class="bi bi-trash me-2"></i>Delete
              </button>
            </div>
          </template>
        </div>
      </div>
    </main>
  </div>
</template>

<script>
// Add Sidebar import
import Sidebar from '@/components/Sidebar.vue'

export default {
  name: 'AssessmentParts',
  components: {
    Sidebar  // Register Sidebar component
  },
  data() {
    return {
      parts: [],
      loading: true,
      error: null,
      headers: [
        { title: 'Question', key: 'question' },
        { title: 'Arabic Question', key: 'question_ar' },
        { title: 'Type', key: 'type' },
        { title: 'Multiple Answers', key: 'allow_multiple' },
      ],
      editingId: null,
      editingValues: {
        title: '',
        title_ar: '',
        description: '',
        description_ar: ''
      },
      showCreateForm: false,
      newPart: {
        title: '',
        title_ar: '',
        description: '',
        description_ar: ''
      },
    }
  },
  computed: {
    processedParts() {
      return this.parts.flatMap(part => [
        { ...part, isPart: true },
        ...part.questions.map(q => ({ ...q, isPart: false }))
      ])
    }
  },
  async created() {
    console.log('Fetching data...')
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('https://api.kewaar.com/api/admin/assessment-parts', {
        headers: {
          'Authorization': `Bearer ${token}`,
        }
      })

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Server error: ${response.status} - ${errorText}`)
      }
      const data = await response.json()
      console.log('API Response:', data)
      this.parts = data
    } catch (err) {
      console.error('API Error:', err)
      this.error = `Failed to load data: ${err.message}`
      if (err.message.includes('401')) {
        this.error += ' - Please check your authentication'
      }
    } finally {
      this.loading = false
    }
  },
  methods: {
    startEditing(part) {
      this.editingId = part.id
      this.editingValues = {
        title: part.title,
        title_ar: part.title_ar,
        description: part.description,
        description_ar: part.description_ar
      }
    },
    cancelEdit() {
      this.editingId = null
      this.editingValues = { 
        title: '', 
        title_ar: '',
        description: '', 
        description_ar: ''
      }
    },
    async savePart(partId) {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`https://api.kewaar.com/api/admin/assessment-parts/${partId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.editingValues)
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        // Update local state
        this.parts = this.parts.map(part => 
          part.id === partId ? { ...part, ...this.editingValues } : part
        )
        this.cancelEdit()
      } catch (err) {
        console.error('Update error:', err)
        this.error = `Failed to update part: ${err.message}`
      }
    },
    async deletePart(partId) {
      if (!confirm('Are you sure you want to delete this part?')) return
      
      try {
        const token = localStorage.getItem('token')
        const response = await fetch(`https://api.kewaar.com/api/admin/assessment-parts/${partId}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        // Remove from local state
        this.parts = this.parts.filter(part => part.id !== partId)
      } catch (err) {
        console.error('Delete error:', err)
        this.error = `Failed to delete part: ${err.message}`
      }
    },
    cancelCreate() {
      this.showCreateForm = false
      this.newPart = { 
        title: '', 
        title_ar: '',
        description: '', 
        description_ar: ''
      }
    },
    async createPart() {
      try {
        const token = localStorage.getItem('token')
        const response = await fetch('https://api.kewaar.com/api/admin/assessment-parts', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(this.newPart)
        })

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)

        const newPart = await response.json()
        this.parts.push(newPart)
        this.cancelCreate()
      } catch (err) {
        console.error('Create error:', err)
        this.error = `Failed to create part: ${err.message}`
      }
    }
  }
}
</script>

<style scoped>
/* Add layout styles */
.assessment-layout {
  display: flex;
  min-height: 100vh;
}

.assessment-content {
  flex: 1;
  margin-left: 280px; /* Match sidebar width */
  padding: 2rem;
  background-color: #f8fafc;
  min-height: 100vh;
}

/* Modify existing styles to match sidebar theme */
.assessment-content h1 {
  color: #1e293b;
  margin-bottom: 2rem;
  font-size: 2rem;
  font-weight: 600;
}

.option-item {
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.question {
  color: #1e293b;
  margin-bottom: 1rem;
  font-size: 1.25rem;
  font-weight: 500;
}

.value-item {
  padding: 0.75rem;
  margin: 0.5rem 0;
  background-color: #f8fafc;
  border-radius: 0.375rem;
}

.order {
  color: #3b82f6;
  font-weight: 600;
}

.arabic {
  color: #64748b;
}

.loading, .error {
  padding: 2rem;
  text-align: center;
  font-size: 1.125rem;
}

.error {
  color: #ef4444;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .assessment-content {
    margin-left: 0;
    padding: 1rem;
  }
  
  .assessment-layout {
    flex-direction: column;
  }
}

/* Add these styles */
.create-form {
  margin-bottom: 2rem;
  padding: 2rem;
  background: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.create-form h2 {
  font-size: 1.5rem;
  margin-bottom: 1.5rem;
  color: #1e293b;
}

.form-group {
  margin-bottom: 1rem;
}

.form-group label {
  display: block;
  margin-bottom: 0.5rem;
  color: #64748b;
}

.form-group input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 1rem;
}

button[type="submit"] {
  background-color: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: background-color 0.2s;
}

button[type="submit"]:disabled {
  background-color: #94a3b8;
  cursor: not-allowed;
}

.success {
  color: #22c55e;
  margin-top: 1rem;
}

/* Add header row styles */
.header-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2rem;
}

.create-button {
  background-color: #3b82f6;
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 0.375rem;
  text-decoration: none;
  transition: background-color 0.2s;
  border: none;
  cursor: pointer;
}

.create-button:hover {
  background-color: #2563eb;
}

.edit-form {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.edit-input {
  padding: 0.5rem;
  border: 1px solid #cbd5e1;
  border-radius: 0.375rem;
  font-size: 1rem;
}

.action-buttons {
  margin-top: 1rem;
  display: flex;
  gap: 0.5rem;
}

.edit-button, .save-button, .cancel-button, .delete-button {
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  cursor: pointer;
  transition: opacity 0.2s;
}

.edit-button {
  background-color: #3b82f6;
  color: white;
  border: none;
}

.save-button {
  background-color: #10b981;
  color: white;
}

.cancel-button {
  background-color: #64748b;
  color: white;
}

.delete-button {
  background-color: #ef4444;
  color: white;
}

button:hover {
  opacity: 0.9;
}
</style> 